// ════════════════════════════════════════════════════════════════════
// Glass-i — Cancel Posted Invoice modal (Phase 2C).
// A posted-invoice cancellation REVERSES the accounting voucher, so it is a
// deliberate, reason-required action — visibly distinct from discarding a draft.
// The backend is authoritative (in-place voucher reversal); this modal only
// captures the mandatory reason and shows the financial context. Shared by the
// invoice editor and the list so the copy + validation stay identical.
// ════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { Modal, Input, Alert, Descriptions } from 'antd';
import { formatMoney } from './invoiceHelpers';

export default function CancelPostedInvoiceModal({ open, invoice, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  // Reset the field whenever the modal (re)opens.
  useEffect(() => { if (open) { setReason(''); setTouched(false); setSubmitting(false); } }, [open]);

  const inv = invoice || {};
  const trimmed = reason.trim();
  const invalid = trimmed.length === 0;

  const handleOk = async () => {
    setTouched(true);
    if (invalid) return;               // block the request on an empty reason
    setSubmitting(true);
    try {
      await onConfirm(trimmed);         // parent performs the API call + reload
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={`Cancel Posted Tax Invoice ${inv.invoiceNo || ''}?`.trim()}
      open={open}
      onCancel={submitting ? undefined : onClose}
      okText="Cancel Posted Invoice"
      cancelText="Back"
      okButtonProps={{ danger: true, loading: submitting, disabled: invalid }}
      cancelButtonProps={{ disabled: submitting }}
      onOk={handleOk}
      maskClosable={!submitting}
      width={520}
      destroyOnClose
    >
      <div className="flex flex-col gap-3">
        <Alert
          type="warning"
          showIcon
          message="This will reverse the accounting voucher and cancel the posted Tax Invoice."
          description="The original invoice and accounting entries remain in the audit trail — nothing is deleted. This is different from discarding a draft: a posted invoice has already affected Accounts and requires voucher reversal."
        />
        <Descriptions size="small" column={1} bordered styles={{ label: { width: 150 } }}
          items={[
            { key: 'no', label: 'Invoice Number', children: inv.invoiceNo || '—' },
            { key: 'cust', label: 'Customer', children: inv.customerName || '—' },
            { key: 'gt', label: 'Grand Total', children: <strong>{formatMoney(inv.grandTotal)}</strong> },
            { key: 'v', label: 'Original Voucher', children: inv.postedVoucherNo || '—' }
          ]}
        />
        <div>
          <div className="mb-1 text-xs font-medium text-slate-600">Cancellation Reason <span className="text-red-500">*</span></div>
          <Input.TextArea
            rows={3}
            maxLength={500}
            showCount
            placeholder="e.g. Customer order cancelled"
            value={reason}
            disabled={submitting}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            status={touched && invalid ? 'error' : undefined}
          />
          {touched && invalid ? (
            <div className="mt-1 text-xs text-red-500">A cancellation reason is required.</div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
